<template>
  <InfuseModal :title="t('e_connector.chargingStationLinks.modal.create.title')" @close="emit('close')">
    <form ref="form" id="create-link" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] w-[600px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.centralSystem')"
            class="basis-1/2"
            size="small"
          >
            <InfuseSelect
              name="centralSystem"
              size="small"
              v-model="centralSystemId"
              :options="centralSystemOptions"
              :label="t('e_connector.chargingStationLinks.modal.field.centralSystem.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStationLink.type')" class="basis-1/2" size="small">
            <template v-if="!mayCreateMaster" #label>
              <InfuseTooltip
                :content="t('e_connector.chargingStationLinks.modal.field.type.tooltip')"
                class="inline cursor-help text-yellow-500"
              >
                <font-awesome-icon :icon="['fal', 'circle-exclamation']" />
              </InfuseTooltip>
            </template>

            <InfuseSelect
              name="type"
              size="small"
              v-model="type"
              :options="typeOptions"
              :required="true"
              :disabled="!mayCreateMaster"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.customChargingStationName')"
            class="basis-full"
            size="small"
            optional
          >
            <InfuseInput
              size="small"
              v-model="customChargingStationName"
              :maxlength="255"
              :placeholder="t('e_connector.chargingStationLinks.modal.field.customChargingStationName.placeholder')"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.commandsAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="commandsAllowed"
              :value="commandsAllowed"
              @on="commandsAllowed = true"
              @off="commandsAllowed = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.authorizationAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="authorizationAllowed"
              :value="authorizationAllowed"
              @on="authorizationAllowed = true"
              @off="authorizationAllowed = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.forwardBasicAuth')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="forwardBasicAuth"
              :value="forwardBasicAuth"
              @on="forwardBasicAuth = true"
              @off="forwardBasicAuth = false"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="create-link" :disabled="loading" :loading="loading">
        {{ t('e_connector.common.action.create') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import {
  InfuseButton,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseSelect,
  InfuseToggle,
  InfuseTooltip
} from '@infuse/ui/components';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CentralSystem, LinkedCentralSystem, LinkedCentralSystemType } from '@infuse/e-connector/central-system/domain';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';

interface Props {
  loading: boolean;
  centralSystems: CentralSystem[];
  chargingStation: ChargingStation;
}

interface Emits {
  (e: 'close'): void;
  (e: 'create', linkedCentralSystem: LinkedCentralSystem): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const centralSystemOptions = computed(() =>
  props.centralSystems
    .filter((centralSystem) =>
      props.chargingStation.linkedCentralSystems.every((lcs) => lcs.centralSystemId !== centralSystem.id)
    )
    .map((centralSystem) => ({
      key: centralSystem.name,
      value: centralSystem.id
    }))
);
const typeOptions = Object.values(LinkedCentralSystemType);

const centralSystemId = ref<number>(centralSystemOptions.value[0]?.value || 0);
const customChargingStationName = ref<string>('');
const commandsAllowed = ref<boolean>(false);
const authorizationAllowed = ref<boolean>(false);
const forwardBasicAuth = ref<boolean>(false);

const mayCreateMaster = computed(
  () =>
    !props.chargingStation.linkedCentralSystems.some(
      (linkedCentralSystem) => linkedCentralSystem.type === LinkedCentralSystemType.MASTER
    )
);

const type = ref<LinkedCentralSystemType>(
  mayCreateMaster.value ? LinkedCentralSystemType.MASTER : LinkedCentralSystemType.READONLY
);

function onSave() {
  const centralSystem = props.centralSystems.find((cs) => cs.id == centralSystemId.value);

  const linkedCentralSystem = {
    centralSystemId: centralSystem?.id,
    chargingStationId: props.chargingStation.id,
    type: type.value,
    customChargingStationName: customChargingStationName.value,
    commandsAllowed: commandsAllowed.value,
    sessionAuthorizationAllowed: authorizationAllowed.value,
    forwardBasicAuth: forwardBasicAuth.value
  } as LinkedCentralSystem;

  emit('create', linkedCentralSystem);
}
</script>
